<template>
  <div v-cloak class="page-content-wrap">
    <FileFullPreviewMode></FileFullPreviewMode>
    <!-- Alerts -->
    <div
      v-if="form.status === 'canceled' && form.canceled_comments"
      class="row row-alert"
    >
      <div class="col-12">
        <div
          class="alert alert-danger alert-content"
          v-html="form.canceled_comments"
        ></div>
      </div>
    </div>

    <div
      v-if="form.status === 'revise' && form.revise_comments"
      class="row row-alert"
    >
      <div class="col-12">
        <div
          class="alert alert-warning alert-content"
          v-html="form.revise_comments"
        ></div>
      </div>
    </div>

    <!-- Signature Step -->
    <transition name="fade">
      <section
        v-if="showSignatureStep"
        ref="signatureStep"
        class="signature-step-wrapper mb-4"
        aria-live="polite"
        aria-label="Pasirašymo žingsnis"
      >
        <header class="signature-step-header">
          <h3 class="signature-step-title">Pasirašykite prašymą</h3>
        </header>

        <article
          v-if="hasSignRules"
          class="signature-rules"
          aria-label="Pasirašymo taisyklės"
        >
          <div class="signature-rules-body" v-html="signRulesContent"></div>
          <p class="signature-rules-hint">
            Tęsdami pasirašymą patvirtinate, kad perskaitėte ir sutinkate su
            šiomis taisyklėmis.
          </p>
        </article>

        <elpako-signature
          :tender-id="form.id"
          :form-data="form.form_data || {}"
          :initial-status="signatureStatus"
          @signature-completed="onSignatureCompleted"
          @signature-error="onSignatureError"
          @status-changed="onSignatureStatusChanged"
        />

        <div class="signature-step-actions">
          <button
            class="btn btn-outline-secondary action-btn-set"
            type="button"
            @click="returnToPreview"
          >
            <i class="fas fa-chevron-left"></i>
            Grįžti į peržiūrą
          </button>

          <button
            v-if="signatureCompleted"
            :disabled="pending"
            class="btn btn-primary greene-light-fill-in action-btn-set"
            type="button"
            @click="submitSignedApplication"
          >
            <i class="fas fa-receipt"></i>
            <span v-if="pending"> Prašome palaukti</span>
            <span v-else> Teikti su parašu</span>
          </button>

          <button
            v-else
            class="btn btn-outline-secondary action-btn-set signature-step-submit-disabled"
            type="button"
            disabled
          >
            <i class="fas fa-lock"></i>
            Pasirašykite, kad tęstumėte
          </button>
        </div>
      </section>
    </transition>

    <!-- Form -->
    <form
      v-show="!showSignatureStep"
      id="tenderRegistration"
      ref="tenderForm"
      class="service-register-form"
      @submit.prevent="tenderRegistration"
    >
      <input type="hidden" name="tender_id" :value="form.id" />

      <!-- Status Display -->
      <div class="row row-service row-tender status-row">
        <div class="col-xs-12 col-md-4 col-name">Būsena</div>
        <div class="col-8 col-value">
          <span v-html="statusRender(form.status || 'draft')"></span>
          <span
            v-if="
              !isEditingAllowed &&
              (form.status === 'draft' || form.status === 'revise')
            "
            class="ml-2 text-danger"
          >
            <i class="fas fa-clock"></i> Pateikimo terminas pasibaigęs
          </span>
          <div
            v-if="
              form.status === 'rejected' || form.status === 'revise_expired'
            "
            class="small text-muted text-comments"
            v-html="form.canceled_comments"
          ></div>
        </div>
      </div>

      <!-- Dynamic attributes -->
      <div
        v-for="(attribute, index) in displayedAttributes"
        :key="attribute?.identifier || index"
        class="row row-service row-tender"
      >
        <!-- Label column -->
        <div class="col-xs-12 col-md-4 col-name">
          <template v-if="attribute.type === 'tender_form_fullname'"
            >Pareiškėjas</template
          >
          <template v-else-if="attribute.type === 'tender_form_email'"
            >El. pašto adresas</template
          >
          <template v-else-if="attribute.type === 'tender_form_price'"
            >Siūloma vietinė rinkliavos suma</template
          >
          <template
            v-else-if="attribute.type === 'tender_form_participation_fee'"
            >Sumokėtas dalyvio mokestis</template
          >
          <template v-else>
            {{ attrLabel(attribute) }}
          </template>
        </div>

        <!-- Value column -->
        <div class="col-8 col-value" :class="valueClasses(attribute)">
          <!-- Built-ins -->
          <template v-if="attribute.type === 'tender_form_fullname'">
            {{ form.user_name }} {{ form.user_surname }}
          </template>

          <template v-else-if="attribute.type === 'tender_form_email'">
            <a
              v-if="form.user_email"
              :href="`mailto:${form.user_email}`"
              class="service-link"
              >{{ form.user_email }}</a
            >
            <span v-else>-</span>
          </template>

          <template v-else-if="attribute.type === 'tender_form_price'">
            <span
              v-if="
                valueOf(attribute) !== undefined && valueOf(attribute) !== null
              "
            >
              {{ formatMoney(valueOf(attribute), form.currency || 'EUR') }}
            </span>
            <span v-else>-</span>
          </template>
          <template
            v-else-if="attribute.type === 'tender_form_participation_fee'"
          >
            <span
              v-if="
                valueOf(attribute) !== undefined && valueOf(attribute) !== null
              "
            >
              {{
                formatMoney(
                  form.paid_participation_fee ?? valueOf(attribute),
                  form.currency || 'EUR',
                )
              }}
            </span>
            <span v-else>-</span>
          </template>

          <!-- Generic types -->
          <template v-else>
            <template v-if="hasValue(attribute)">
              <!-- Special: notify_use_alt_email should render Taip/Ne regardless of control type -->
              <template
                v-if="
                  attribute.key === 'notify_use_alt_email' ||
                  attribute.type === 'notify_use_alt_email'
                "
              >
                {{ toggleLabelByValue(isTruthy(valueOf(attribute))) }}
              </template>

              <!-- Special: tender_form_email_notify displayed as email link when present -->
              <template
                v-else-if="
                  attribute.key === 'tender_form_email_notify' ||
                  attribute.type === 'tender_form_email_notify'
                "
              >
                <a
                  v-if="valueOf(attribute)"
                  :href="`mailto:${valueOf(attribute)}`"
                  class="service-link"
                  >{{ valueOf(attribute) }}</a
                >
                <template v-else>-</template>
              </template>

              <template
                v-else-if="
                  attribute.type === 'select' || attribute.type === 'radio'
                "
              >
                <!-- For notify_use_alt_email shown as select/radio without labels in options, fallback to Taip/Ne -->
                <template v-if="attribute.key === 'notify_use_alt_email'">
                  {{ toggleLabelByValue(isTruthy(valueOf(attribute))) }}
                </template>
                <template v-else>
                  {{
                    optionNameById(
                      normalizeOptions(attribute.options),
                      valueOf(attribute),
                    ) || '-'
                  }}
                </template>
              </template>

              <template v-else-if="attribute.type === 'checkbox'">
                <div
                  v-for="(label, i) in selectedCheckBoxOptions(
                    normalizeOptions(attribute.options),
                    valueOf(attribute),
                  )"
                  :key="i"
                >
                  {{ label }}
                </div>
              </template>

              <template v-else-if="attribute.type === 'toggle'">
                {{ toggleLabelByValue(valueOf(attribute)) }}
              </template>

              <template v-else-if="attribute.type === 'email'">
                <a
                  :href="`mailto:${valueOf(attribute)}`"
                  class="service-link"
                  >{{ valueOf(attribute) }}</a
                >
              </template>

              <template v-else-if="attribute.type === 'wysiwyg'">
                <div v-html="valueOf(attribute)"></div>
              </template>

              <template v-else-if="attribute.type === 'upload'">
                <file-preview
                  :folder="valueOf(attribute)"
                  :show-file-name="
                    attribute.show_file_name != null
                      ? attribute.show_file_name
                      : 1
                  "
                  :show-file-size="
                    attribute.show_file_size != null
                      ? attribute.show_file_size
                      : 1
                  "
                  :show-file-extension="
                    attribute.show_file_extension != null
                      ? attribute.show_file_extension
                      : 1
                  "
                />
              </template>

              <template v-else>
                {{ valueOf(attribute) }}
              </template>
            </template>

            <template v-else>
              <!-- When no value: show Taip/Ne for notify_use_alt_email; show hyphen for alt email -->
              <template
                v-if="
                  attribute.key === 'notify_use_alt_email' ||
                  attribute.type === 'notify_use_alt_email'
                "
              >
                {{ toggleLabelByValue(isTruthy(valueOf(attribute))) }}
              </template>
              <template
                v-else-if="
                  attribute.key === 'tender_form_email_notify' ||
                  attribute.type === 'tender_form_email_notify'
                "
              >
                -
              </template>
              <template v-else-if="attribute.type === 'toggle'">
                {{ toggleLabelByValue(valueOf(attribute)) }}
              </template>
              <template v-else>-</template>
            </template>
          </template>
        </div>
      </div>

      <!-- Actions -->
      <div class="row row-service">
        <div class="col-xs-12 col-md-4 col-name"></div>
        <div class="col-xs-12 col-md-8 col-value">
          <a
            href="/account/tenders"
            class="btn btn-primary blue-light-fill-in mr-2 action-btn-set"
          >
            <i class="fas fa-chevron-left"></i> Mano prašymai
          </a>

          <a
            v-if="canEdit"
            :href="`/account/tenders/${form.id}/edit`"
            class="btn btn-warning blue mr-2 action-btn-set"
          >
            <i class="fas fa-pencil-alt"></i> Koreguoti
          </a>

          <button
            v-if="canSubmit && !showSignatureStep"
            :disabled="pending"
            class="btn btn-warning greene-light-fill-in mr-2 action-btn-set"
            type="button"
            @click="proceedToSignature"
          >
            <i class="fas fa-pen-alt"></i>
            <span v-if="pending"> Prašome palaukti</span>
            <span v-else> Pereiti prie parašymo</span>
          </button>

          <!-- Download signed document on preview page when application is registered and signature is signed -->
          <a
            v-if="
              (form.status === 'register' || form.status === 'approved') &&
              (signatureStatus === 'signed' || signatureStatus === 'success')
            "
            class="btn btn-outline-secondary action-btn-set"
            :href="`/account/tenders/${form.encoded_id || form.id}/signature/download-latest`"
          >
            <i class="fas fa-download"></i>
            <span> Atsisiųsti pasirašytą dokumentą </span>
          </a>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import form from '@core/js/mixins/form'
import _ from 'lodash'
import axios from 'axios'
import ElpakoSignature from './elpako-signature'
import FilePreview from '@assets/js/views/Preview/FilePreview'
import FileFullPreviewMode from '@assets/js/components/Preview/FileFullPreviewMode'

export default {
  name: 'TenderRegistration',
  components: {
    ElpakoSignature,
    FilePreview,
    FileFullPreviewMode,
  },
  mixins: [form],
  props: {
    tender: { type: Object, default: () => ({}) },
    form: { type: Object, default: () => ({}) },
    // Allow backend to explicitly pass juridical flag like tender-form.vue
    isJuridical: { type: [Boolean, Number, String], default: null },
    signRules: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      pending: false,
      // eslint-disable-next-line vue/no-reserved-keys
      _submitCancel: null,
      showSignatureStep: false,
      signatureStatus: 'pending_signature',
      signatureCompleted: false,
      signatureData: null,
    }
  },
  computed: {
    effectiveStatus() {
      return this.form?.status || 'unknown'
    },
    isEditingAllowed() {
      // Check if the tender has an envelope_open_at field and if deadline has passed
      if (!this.tender?.envelope_open_at) {
        return true // No deadline set, editing allowed
      }

      const now = new Date()
      const envelopeOpenAt = new Date(this.tender.envelope_open_at)
      return now <= envelopeOpenAt // Allow editing only if deadline hasn't passed
    },
    // Filtered attributes to render: remove excluded types, hidden by conditions, and empty values
    displayedAttributes() {
      return (this.formStructure || []).filter((attr) =>
        this.shouldRender(attr),
      )
    },
    showStatusBadge() {
      return !!(this.form?.locked || this.form?.status === 'revise')
    },
    signRulesContent() {
      if (!this.signRules) return ''
      return this.signRules.content_html || this.signRules.content || ''
    },
    hasSignRules() {
      return (
        typeof this.signRulesContent === 'string' &&
        this.signRulesContent.trim().length > 0
      )
    },
    canEdit() {
      return !!this.form?.can_edit && this.isEditingAllowed
    },
    canSubmit() {
      return (
        !this.form?.locked &&
        this.form?.status !== 'rejected' &&
        this.isEditingAllowed
      )
    },
    // Use normalized structure everywhere
    formStructure() {
      const raw = this.form?.form_structure
      if (Array.isArray(raw)) return raw
      if (typeof raw === 'string' && raw.trim().length) {
        try {
          const parsed = JSON.parse(raw)
          return Array.isArray(parsed) ? parsed : []
        } catch {
          return []
        }
      }
      return []
    },
  },
  mounted() {
    // When opening the preview (submit-review) page, fetch current signature status
    this.loadSignatureStatus()
  },
  beforeDestroy() {
    // If submission is in-flight, cancel it and re-enable the button
    try {
      if (this._submitCancel) {
        this._submitCancel.cancel('Navigation away')
      }
    } catch (e) {
      /* empty */
    }
    this.pending = false
    this._submitCancel = null
  },
  methods: {
    async loadSignatureStatus() {
      try {
        const hashedId = this.form?.encoded_id || this.form?.id
        if (!hashedId) return

        const { data } = await axios.get(
          `/account/tenders/${hashedId}/signature/status`,
        )

        const status = data?.status || data?.data?.status || 'pending_signature'
        this.signatureStatus = status
        this.signatureCompleted = status === 'signed'
      } catch (e) {
        // Ignore; fall back to default pending state
      }
    },
    // Compute classes for value cell (e.g., dim primary email when alt email used)
    valueClasses(attr) {
      const classes = []
      if (
        (attr?.type === 'tender_form_email' ||
          attr?.key === 'tender_form_email') &&
        this.isAltEmailEnabled()
      ) {
        classes.push('text-muted')
      }
      return classes
    },

    // Get human label for generics
    attrLabel(attr) {
      return (
        (attr?.name &&
          (attr.name[this.$app?.lang] || attr.name.lt || attr.name.en)) ||
        '—'
      )
    },

    // Options may be: array | stringified JSON | "undefined" | undefined
    normalizeOptions(options) {
      if (!options || options === 'undefined') return []
      if (Array.isArray(options)) return options
      if (typeof options === 'string') {
        try {
          const parsed = JSON.parse(options)
          return Array.isArray(parsed) ? parsed : []
        } catch {
          return []
        }
      }
      return []
    },

    // Simple money display; if you later switch to cents, adjust here
    formatMoney(number, currency = 'EUR') {
      const n = Number(number)
      if (!Number.isFinite(n)) return `${number} ${currency}`
      try {
        return new Intl.NumberFormat(this.$app?.locale || 'lt-LT', {
          style: 'currency',
          currency,
        }).format(n)
      } catch {
        return `${n.toFixed(2)} ${currency}`
      }
    },
    isGenericField(attr) {
      if (!attr || !attr.type) return false
      const types = [
        'text',
        'email',
        'textarea',
        'wysiwyg',
        'number',
        'toggle',
        'select',
        'radio',
        'checkbox',
        'upload',
        'file',
        'image',
      ]
      return types.includes(attr.type)
    },
    valueOf(attr) {
      if (!attr?.identifier) return undefined
      return this.form?.form_data?.[attr.identifier]
    },
    hasValue(attr) {
      const v = this.valueOf(attr)
      if (v === undefined || v === null) return false
      if (typeof v === 'string') return v.trim().length > 0
      if (Array.isArray(v)) return v.length > 0
      return true
    },
    optionNameById(options, identifier) {
      if (!options || identifier === undefined || identifier === null) return ''
      const found = _.find(options, (o) => String(o?.id) === String(identifier))
      return found ? (found.text ?? found.name ?? '') : ''
    },
    selectedCheckBoxOptions(options, identifiers) {
      if (!options || !Array.isArray(identifiers)) return []
      return identifiers
        .map((id) => _.find(options, (o) => String(o?.id) === String(id)))
        .filter(Boolean)
        .map((opt) => opt.text ?? opt.name ?? '')
    },
    toggleLabelByValue(value) {
      return value ? 'Taip' : 'Ne'
    },
    // Normalize common truthy values
    isTruthy(val) {
      if (val === true || val === 1 || val === '1') return true
      if (typeof val === 'string') {
        const s = val.trim().toLowerCase()
        if (['true', 'yes', 'y', 'taip', '1'].includes(s)) return true
        if (['false', 'no', 'n', 'ne', '0'].includes(s)) return false
      }
      if (typeof val === 'number') return val === 1
      return !!val
    },

    // Conditional visibility similar to tender-form
    canDisplay(attr) {
      const key = attr?.key || ''
      const type = attr?.type || ''
      // Hide alt email field when toggle is off
      if (
        key === 'tender_form_email_notify' ||
        type === 'tender_form_email_notify'
      ) {
        return this.isAltEmailEnabled()
      }
      // Show Sodra/VMI only for juridical
      if (
        key === 'certificate_from_sodra' ||
        type === 'certificate_from_sodra' ||
        key === 'certificate_from_vmi' ||
        type === 'certificate_from_vmi'
      ) {
        return this.isJuridicalUser()
      }
      // Show personal id copy only for natural persons
      if (key === 'personal_id_copy' || type === 'personal_id_copy') {
        return !this.isJuridicalUser()
      }
      return true
    },
    // Master predicate whether an attribute should render at all
    shouldRender(attr) {
      if (!attr) return false
      // Exclude purpose/description/minimum fee from registration view
      if (
        ['tender_purpose', 'tender_description', 'minimum_fee'].includes(
          attr.type,
        )
      ) {
        return false
      }
      // Always show the notify_use_alt_email toggle row
      if (
        attr.key === 'notify_use_alt_email' ||
        attr.type === 'notify_use_alt_email'
      ) {
        return true
      }
      // Show alt email row when toggle is enabled, even if value is empty
      if (
        attr.key === 'tender_form_email_notify' ||
        attr.type === 'tender_form_email_notify'
      ) {
        return this.isAltEmailEnabled()
      }
      // Apply conditional visibility rules
      if (!this.canDisplay(attr)) return false

      // Built-ins
      if (attr.type === 'tender_form_fullname') {
        return !!(this.form?.user_name || this.form?.user_surname)
      }
      if (attr.type === 'tender_form_email') {
        return !!this.form?.user_email
      }
      if (attr.type === 'tender_form_price') {
        return this.hasValue(attr)
      }
      if (attr.type === 'tender_form_participation_fee') {
        return (
          this.hasValue(attr) ||
          Number.isFinite(this.form?.paid_participation_fee)
        )
      }

      // Always show tender docs rows even when empty
      const docKeys = [
        'payment_receipt',
        'certificate_from_sodra',
        'certificate_from_vmi',
        'personal_id_copy',
      ]
      if (docKeys.includes(attr?.key)) {
        return true
      }

      // Generic controls: show only when we have a meaningful value
      if (this.isGenericField(attr)) {
        if (attr.type === 'toggle') {
          // Always show toggle when a value is present (including false)
          const v = this.valueOf(attr)
          return v !== undefined && v !== null
        }
        return this.hasValue(attr)
      }

      // Otherwise default to hidden
      return false
    },

    // Alt email toggle detector (based on form_structure + form_data)
    isAltEmailEnabled() {
      // Find the identifier for notify_use_alt_email
      const toggleAttr = (this.formStructure || []).find(
        (a) =>
          a &&
          (a.key === 'notify_use_alt_email' ||
            a.type === 'notify_use_alt_email'),
      )
      const id = toggleAttr && toggleAttr.identifier
      // Prefer identified value; fallback to direct key in form_data
      let v = id
        ? this.form?.form_data?.[id]
        : this.form?.form_data?.notify_use_alt_email
      if (typeof v === 'string') {
        const s = v.trim().toLowerCase()
        if (['yes', 'taip', '1', 'true'].includes(s)) return true
        if (['no', 'ne', '0', 'false'].includes(s)) return false
      }
      if (typeof v === 'number') return v === 1
      if (typeof v === 'boolean') return v
      return false
    },

    // Juridical detector similar to tender-form
    isJuridicalUser() {
      // 0) Prefer value stored on the form record itself
      let v = this.form?.is_juridical
      // 1) Prefer explicit prop if form doesn't have it
      if (v === null || v === undefined) v = this.isJuridical
      // 2) Fallback to Vuex userAuth module
      if (v === null || v === undefined) {
        const user = this.$store?.getters?.['userAuth/user']
        v = user?.is_juridical ?? user?.juridical ?? user?.isJuridical
      }
      if (typeof v === 'string') {
        const s = v.trim().toLowerCase()
        if (['1', 'true', 'yes', 'taip'].includes(s)) return true
        if (['0', 'false', 'no', 'ne'].includes(s)) return false
      }
      if (typeof v === 'number') return v === 1
      if (typeof v === 'boolean') return v
      return false
    },
    async tenderRegistration() {
      if (this.pending) return
      this.pending = true

      const formEl = this.$refs.tenderForm
      let source = null
      try {
        // Keep application/x-www-form-urlencoded for backend compatibility
        const body = new URLSearchParams(new FormData(formEl))

        // Create a cancel token so we can abort on destroy
        source = axios.CancelToken.source()
        this._submitCancel = source

        const { data } = await axios.post(
          `/account/tenders/${this.form.id}/submit-request`,
          body,
          {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            cancelToken: source.token,
          },
        )

        if (data?.status === 'success') {
          // ensure complete DOM updates before redirect
          this.$nextTick(() => (window.location.href = '/account/tenders'))
        } else {
          // optional: show a generic error toast
          // this.$bvToast?.toast(this.$t('errors.unknown'), { variant: 'danger' })
        }
      } catch (error) {
        if (!axios.isCancel?.(error)) {
          // optional: show backend error message if available
          // const msg = error?.response?.data?.message || this.$t('errors.request_failed')
          // this.$bvToast?.toast(msg, { variant: 'danger' })
        }
      } finally {
        this.pending = false
        this._submitCancel = null
      }
    },

    // Signature-related methods
    proceedToSignature() {
      this.showSignatureStep = true

      this.$nextTick(() => {
        const el = this.$refs.signatureStep
        if (!el) return

        const options = {
          container: 'body',
          duration: 400,
          easing: 'ease-in-out',
          offset: -150,
          force: true,
          cancelable: true,
          x: false,
          y: true,
        }

        // Prefer vue-scrollto if available
        if (typeof this.$scrollTo === 'function') {
          this.$scrollTo(el, 400, options)
        } else {
          const rect = el.getBoundingClientRect()
          const top = rect.top + window.pageYOffset + (options.offset || 0)
          window.scrollTo({ top, behavior: 'smooth' })
        }
      })
    },

    returnToPreview() {
      this.showSignatureStep = false
    },

    submitSignedApplication() {
      if (this.pending || !this.signatureCompleted) return
      this.tenderRegistration()
    },

    onSignatureCompleted(signatureData) {
      this.signatureCompleted = true
      this.signatureData = signatureData
      this.signatureStatus = 'signed'
    },

    onSignatureError(errorData) {
      this.signatureCompleted = false
      this.signatureStatus = 'error'
      console.error('Signature error:', errorData)
    },

    onSignatureStatusChanged(newStatus) {
      this.signatureStatus = newStatus
      this.signatureCompleted = newStatus === 'signed'
    },

    // Legacy helper kept (not used internally)
    statusRender(status) {
      return (
        '<span class="badge badge-' +
        status +
        '">' +
        this.$t('tenders.status.' + status) +
        '<span>'
      )
    },
  },
}
</script>

<style scoped>
[v-cloak] {
  display: none;
}
:deep(.card) {
  color: #0c233a !important;
}

.signature-step-wrapper {
  background: #f8f9fa;
  padding: 2rem;
  border-radius: 0.5rem;
  border: 1px solid #dee2e6;
  margin: 2rem 0;
}
.signature-rules {
  background: #ffffff;
  border: 1px solid #e2e6ea;
  border-radius: 0.375rem;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.signature-rules-body {
  max-height: 220px;
  overflow-y: auto;
  font-size: 0.95rem;
  color: #212529;
  text-align: justify;
  padding: 0 10px 10px 10px;
}

.signature-rules-hint {
  margin-top: 1rem;
  margin-bottom: 0;
  font-size: 1rem;
  color: #ed6b5b;
}
.signature-step-header {
  margin-bottom: 1.5rem;
}

.signature-step-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.signature-step-description {
  margin-bottom: 0;
  color: #6c757d;
}

.signature-step-wrapper :deep(.elpako-signature-step) {
  background: white;
  padding: 1.5rem;
  border-radius: 0.375rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.signature-step-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.signature-step-actions .btn {
  min-width: 180px;
}

.signature-step-submit-disabled {
  cursor: not-allowed;
  opacity: 0.75;
}

@media (max-width: 576px) {
  .signature-step-wrapper {
    padding: 1.25rem;
    margin: 0 0;
  }

  .signature-step-actions {
    flex-direction: column;
  }
}

.signature-step-title {
  margin-top: 0;
  margin-bottom: 0;
  padding-bottom: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
